import { Component, computed, inject } from '@angular/core';
import {
  BannerConfig,
  BannerStyle,
  ButtonComponent,
  ButtonType,
  CardComponent,
  ConfirmationDialogComponent,
  ContentHeaderComponent,
  DateUtils,
  DialogUtils,
  GolfIndustryHoursStatus,
  InfoLabelComponent,
  InputSize,
  LargeBannerComponent,
  NavBarComponent,
  PhoneNumberUtils,
  ReadonlyFormFieldComponent,
  SHARED_CONFIG,
  StatusPillHelper,
} from '@pgaoc/shared-lib';
import { ReviewGolfIndustryHoursViewModel } from './review-golf-industry-hours-view-model';
import { DatePipe, DecimalPipe } from '@angular/common';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { RejectDialogComponent } from './reject-dialog/reject-dialog.component';

@Component({
  selector: 'app-review-golf-industry-hours',
  imports: [
    ButtonComponent,
    ContentHeaderComponent,
    NavBarComponent,
    CardComponent,
    DatePipe,
    DecimalPipe,
    InfoLabelComponent,
    ReadonlyFormFieldComponent,
    LargeBannerComponent,
  ],
  templateUrl: './review-golf-industry-hours.component.html',
  styleUrl: './review-golf-industry-hours.component.scss',
  providers: [ReviewGolfIndustryHoursViewModel],
})
export class ReviewGolfIndustryHoursComponent {
  protected readonly ButtonType = ButtonType;
  protected readonly GolfIndustryHoursStatus = GolfIndustryHoursStatus;
  protected readonly InputSize = InputSize;
  protected readonly PhoneNumberUtils = PhoneNumberUtils;
  protected readonly DateUtils = DateUtils;
  protected readonly StatusPillHelper = StatusPillHelper;
  protected readonly config = inject(SHARED_CONFIG);
  protected readonly viewModel = inject(ReviewGolfIndustryHoursViewModel);
  private readonly dialog = inject(MatDialog);

  readonly bannerConfig = computed((): BannerConfig | undefined => {
    // can't use `statusId` because the backend doesn't return it on PUT
    if (this.viewModel.golfIndustryHours().status?.id === GolfIndustryHoursStatus.Approved) {
      return {
        title: $localize`These industry hours have been approved.`,
        icon: 'check-circle',
        style: BannerStyle.Success,
      };
    } else if (this.viewModel.golfIndustryHours().status?.id === GolfIndustryHoursStatus.Denied) {
      return {
        title: $localize`These industry hours have been rejected.`,
        icon: 'cancel-circle',
        style: BannerStyle.Error,
      };
    } else {
      return undefined;
    }
  });

  navbarButtonClicked() {
    window.open(`https://www.${this.config.pgaSiteUrl}`, '_blank');
  }

  approveClicked() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, DialogUtils.smallDialogConfig());
    dialogRef.componentInstance.config.set({
      title: $localize`Approve Golf Industry Hours`,
      message: $localize`Are you sure you want to approve these golf industry hours?`,
      secondaryButtonText: $localize`Cancel`,
      primaryButtonText: $localize`Approve Submission`,
    });
    dialogRef.afterClosed().subscribe({
      next: result => {
        if (!result) return;
        this.viewModel.updateStatus(GolfIndustryHoursStatus.Approved);
      },
    });
  }

  denyClicked() {
    const dialogRef = this.dialog.open<RejectDialogComponent, MatDialogConfig, string | undefined>(
      RejectDialogComponent,
      DialogUtils.smallDialogConfig(),
    );
    dialogRef.afterClosed().subscribe({
      next: result => {
        if (result === undefined) return;
        this.viewModel.updateStatus(GolfIndustryHoursStatus.Denied, result);
      },
    });
  }
}
